import React from 'react';
import { Link } from 'react-router-dom';
import { Users, Briefcase, Clock, Shield, CheckCircle, ArrowRight, Award } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { staffImages } from '../utils/mockData';

const Staffing = () => {
  const staffingServices = [
    {
      icon: Users,
      title: 'Registered Mental Health Nurses (RMN)',
      description: 'Qualified mental health nurses with expertise in complex care, risk assessment, and clinical leadership.',
      available: 'Permanent & Bank'
    },
    {
      icon: Users,
      title: 'Registered General Nurses (RGN)',
      description: 'Experienced general nurses providing physical health monitoring and medication support.',
      available: 'Bank & Temporary'
    },
    {
      icon: Award,
      title: 'Healthcare Assistants (HCA)',
      description: 'Trained healthcare assistants supporting personal care, daily living, and wellbeing activities.',
      available: 'Permanent & Bank'
    },
    {
      icon: Briefcase,
      title: 'Support Workers',
      description: 'Experienced support workers providing practical and emotional support for community living.',
      available: 'Permanent & Bank'
    }
  ];

  const benefits = [
    {
      icon: Shield,
      title: 'Fully Vetted Staff',
      description: 'All staff undergo rigorous DBS checks, reference checks, and safeguarding training.'
    },
    {
      icon: CheckCircle,
      title: 'Qualified Professionals',
      description: 'NMC-registered nurses and Care Certificate-trained healthcare assistants and support workers.'
    },
    {
      icon: Clock,
      title: 'Flexible Arrangements',
      description: 'Permanent placements, temporary cover, and staff bank solutions to meet your needs.'
    },
    {
      icon: Award,
      title: 'Ongoing Support',
      description: 'Regular supervision, training updates, and clinical governance oversight.'
    }
  ];

  const whyChooseUs = [
    'RMN-led clinical leadership and oversight',
    'Rigorous recruitment and vetting processes',
    'Mandatory training and ongoing professional development',
    'Understanding of mental health needs and recovery principles',
    'Flexible staffing models to suit your service',
    'Commitment to safe, compassionate, evidence-based care'
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#E8F4FC] to-[#F0EDF7] py-20">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h1 className="heading-hero">Healthcare Staffing Solutions</h1>
              <p className="body-large">
                High-quality temporary and permanent staffing for mental health and community care services across the UK.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact">
                  <Button size="lg" className="bg-gradient-to-r from-[#4A90E2] to-[#7B68BE]">
                    Request Staff
                  </Button>
                </Link>
                <Link to="/recruitment">
                  <Button 
                    size="lg" 
                    variant="outline" 
                    className="border-2 border-[#4A90E2] text-[#4A90E2] hover:bg-[#4A90E2] hover:text-white"
                  >
                    Join Our Team
                  </Button>
                </Link>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              {staffImages.map((img, index) => (
                <img 
                  key={index}
                  src={img}
                  alt={`Healthcare professional ${index + 1}`}
                  className="rounded-xl w-full h-64 object-cover shadow-lg"
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Staffing Services */}
      <section className="section bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="heading-2 mb-4">Our Staffing Solutions</h2>
            <p className="body-large max-w-2xl mx-auto">
              Professional healthcare staff for mental health and community care providers.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-6xl mx-auto">
            {staffingServices.map((service, index) => {
              const Icon = service.icon;
              return (
                <Card key={index} className="p-8 border-0 hover:shadow-xl transition-all">
                  <div className="w-14 h-14 bg-gradient-to-br from-[#4A90E2] to-[#7B68BE] rounded-xl flex items-center justify-center mb-6">
                    <Icon className="text-white" size={28} />
                  </div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="heading-3">{service.title}</h3>
                  </div>
                  <p className="body-medium text-gray-600 mb-4">{service.description}</p>
                  <div className="inline-block bg-[#E8F4FC] text-[#4A90E2] px-4 py-2 rounded-full text-sm font-semibold">
                    {service.available}
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="section bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="heading-2 mb-4">Why Choose Our Staffing Services?</h2>
            <p className="body-large max-w-2xl mx-auto">
              Quality, reliability, and professionalism in every placement.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div 
                  key={index}
                  className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-all"
                >
                  <div className="w-12 h-12 bg-[#E8F4FC] rounded-lg flex items-center justify-center mb-4">
                    <Icon className="text-[#4A90E2]" size={24} />
                  </div>
                  <h3 className="font-semibold text-lg mb-3 text-gray-900">{benefit.title}</h3>
                  <p className="text-sm text-gray-600">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-[#E8F4FC] to-[#F0EDF7] rounded-2xl p-8 md:p-12">
              <h2 className="heading-2 mb-8 text-center">Our Commitment to Excellence</h2>
              <div className="grid md:grid-cols-2 gap-4">
                {whyChooseUs.map((item, index) => (
                  <div key={index} className="flex items-start gap-3 bg-white/80 backdrop-blur-sm rounded-lg p-4">
                    <CheckCircle className="text-[#4A90E2] flex-shrink-0 mt-1" size={20} />
                    <span className="text-gray-800">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Models */}
      <section className="section bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="heading-2 mb-8 text-center">Flexible Staffing Models</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <Card className="p-6 border-0">
                <div className="text-center">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#4A90E2] to-[#7B68BE] rounded-full flex items-center justify-center mx-auto mb-4">
                    <Briefcase className="text-white" size={24} />
                  </div>
                  <h3 className="heading-3 mb-3">Permanent Placement</h3>
                  <p className="text-sm text-gray-600">
                    Full-time or part-time permanent staff recruitment for your care service.
                  </p>
                </div>
              </Card>
              <Card className="p-6 border-0">
                <div className="text-center">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#4A90E2] to-[#7B68BE] rounded-full flex items-center justify-center mx-auto mb-4">
                    <Clock className="text-white" size={24} />
                  </div>
                  <h3 className="heading-3 mb-3">Temporary Cover</h3>
                  <p className="text-sm text-gray-600">
                    Short-term or long-term cover for sickness, leave, or service gaps.
                  </p>
                </div>
              </Card>
              <Card className="p-6 border-0">
                <div className="text-center">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#4A90E2] to-[#7B68BE] rounded-full flex items-center justify-center mx-auto mb-4">
                    <Users className="text-white" size={24} />
                  </div>
                  <h3 className="heading-3 mb-3">Staff Bank</h3>
                  <p className="text-sm text-gray-600">
                    Access to a pool of qualified professionals for flexible shift coverage.
                  </p>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section bg-gradient-to-r from-[#4A90E2] to-[#7B68BE] text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <h2 className="heading-2 text-white">Need Reliable Healthcare Staff?</h2>
            <p className="body-large text-white/90">
              Contact us to discuss your staffing needs and how we can support your service.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button size="lg" className="bg-white text-[#4A90E2] hover:bg-gray-100">
                  Request Staff Now
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
              <Link to="/recruitment">
                <Button 
                  size="lg" 
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white hover:text-[#4A90E2]"
                >
                  Join Our Staff Bank
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Staffing;
